/**
 * Plugin API Client
 * Handles plugin instance and data storage via backend
 */

import { env } from '@/lib/env';
import { getAuthItem } from '@/lib/authStorage';
import { getActiveWorkspaceId, getSelectedWorkspaceId } from '@/lib/api/workspace-context';
import type { PluginToolDefinition } from './pluginTypes';

export interface PluginInstanceData {
  id: string;
  pluginId: string;
  conversationId: string;
  workspaceId: string;
  config: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  version: number;
}

/** Tool schema for serialization (without execute function) */
export interface PluginToolSchema {
  name: string;
  description: string;
  parameters: {
    type: 'object';
    properties: Record<string, {
      type: 'string' | 'number' | 'boolean' | 'array' | 'object';
      description?: string;
      enum?: string[];
    }>;
    required?: string[];
  };
}

/** Manifest containing tool definitions for chat integration */
export interface PluginManifest {
  pluginId: string;
  chatKeywords: string[];
  tools: PluginToolSchema[];
}

interface PluginData {
  data: Record<string, unknown>;
  fetchedAt: string;
  expiresAt?: string;
}

function getAuthToken(): string | null {
  return getAuthItem('xavior_auth_token');
}

function getUserId(): string | null {
  return getAuthItem('userId');
}

function getHeaders(): HeadersInit {
  const token = getAuthToken();
  const userId = getUserId();
  // #2736 — workspace-scoped routes require X-Workspace-Id (the JWT or this
  // header). Match the canonical apiClient.ts behavior so the backend can
  // resolve workspace context for plugin API calls.
  const workspaceId = getSelectedWorkspaceId();
  return {
    'Content-Type': 'application/json',
    // JWT auth for production
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    // X-User-Id fallback for dev bypass mode
    ...(userId ? { 'X-User-Id': userId } : {}),
    ...(workspaceId ? { 'X-Workspace-Id': workspaceId } : {}),
  };
}

function normalizePluginInstance(raw: Record<string, unknown>): PluginInstanceData {
  const conversationId =
    typeof raw.conversationId === 'string' && raw.conversationId.trim().length > 0
      ? raw.conversationId.trim()
      : '';
  return {
    ...raw,
    conversationId,
  } as PluginInstanceData;
}

/**
 * List plugin instances for a conversation scope.
 */
export async function listPluginInstances(conversationId: string): Promise<PluginInstanceData[]> {
  const search = new URLSearchParams({ conversationId });
  const response = await fetch(`${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/plugins/instances?${search.toString()}`, {
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to list plugin instances: ${response.status}`);
  }

  const { instances } = await response.json();
  return (instances as Record<string, unknown>[]).map(normalizePluginInstance);
}

/**
 * Create a plugin instance
 */
export async function createPluginInstance(
  pluginId: string,
  conversationId: string,
  config: Record<string, unknown>
): Promise<PluginInstanceData> {
  const response = await fetch(`${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/plugins/instances`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ pluginId, conversationId, config }),
  });

  if (!response.ok) {
    throw new Error(`Failed to create plugin instance: ${response.status}`);
  }

  const { instance } = await response.json();
  return normalizePluginInstance(instance as Record<string, unknown>);
}

/**
 * Update a plugin instance config
 */
export async function updatePluginInstance(
  instanceId: string,
  config: Record<string, unknown>
): Promise<PluginInstanceData> {
  const response = await fetch(`${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/plugins/instances/${instanceId}`, {
    method: 'PATCH',
    headers: getHeaders(),
    body: JSON.stringify({ config }),
  });

  if (!response.ok) {
    throw new Error(`Failed to update plugin instance: ${response.status}`);
  }

  const { instance } = await response.json();
  return normalizePluginInstance(instance as Record<string, unknown>);
}

/**
 * Delete a plugin instance
 */
export async function deletePluginInstance(instanceId: string): Promise<void> {
  const response = await fetch(`${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/plugins/instances/${instanceId}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to delete plugin instance: ${response.status}`);
  }
}

/**
 * Get cached plugin data from plugin-specific database
 */
export async function getPluginData(
  instanceId: string,
  dataType: string
): Promise<PluginData | null> {
  const response = await fetch(
    `${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/plugins/instances/${instanceId}/data/${dataType}`,
    { headers: getHeaders() }
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`Failed to get plugin data: ${response.status}`);
  }

  // Response format: { data, fetchedAt, expiresAt }
  return await response.json();
}

/**
 * Save plugin data to plugin-specific database
 */
export async function savePluginData(
  instanceId: string,
  dataType: string,
  data: Record<string, unknown>,
  expiresInMinutes?: number
): Promise<{ success: boolean; savedAt: string }> {
  const response = await fetch(
    `${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/plugins/instances/${instanceId}/data/${dataType}`,
    {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify({ data, expiresInMinutes }),
    }
  );

  if (!response.ok) {
    throw new Error(`Failed to save plugin data: ${response.status}`);
  }

  // Response format: { success, dataType, savedAt }
  return await response.json();
}

/**
 * Convert PluginToolDefinition to serializable PluginToolSchema
 * Strips the execute function which can't be serialized
 */
export function toToolSchema(tool: PluginToolDefinition): PluginToolSchema {
  return {
    name: tool.name,
    description: tool.description,
    parameters: tool.parameters,
  };
}

/**
 * Register a plugin's tool manifest for chat integration.
 * This enables the main chat agent to discover and call plugin tools.
 */
export async function registerPluginManifest(
  instanceId: string,
  manifest: PluginManifest
): Promise<void> {
  const response = await fetch(
    `${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/plugins/instances/${instanceId}/manifest`,
    {
      method: 'PUT',
      headers: getHeaders(),
      body: JSON.stringify(manifest),
    }
  );

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(`Failed to register plugin manifest: ${error.error || response.status}`);
  }
}
